import { useCallback, useMemo } from "react";

import { useGetFocusAreaList } from "@/domain/focus-area/hooks/api/use-get-focus-area-list";
import { useGetLabelGroupListForWorkspace } from "@/domain/label/hooks/api/use-get-label-group-list-for-workspace";
import { useGetLabelListForWorkspace } from "@/domain/label/hooks/api/use-get-label-list-for-workspace";
import { useGetPhaseListForWorkspace } from "@/domain/phase/hooks/api/use-get-phase-list-for-workspace";
import { useGetPriorityListForWorkspace } from "@/domain/priority/hooks/api/use-get-priority-list-for-workspace";
import { useGetStatusListForWorkspace } from "@/domain/status/hooks/api/use-get-status-list-for-workspace";
import { useGetUnitList } from "@/domain/unit/hooks/api/use-get-unit-list";
import { useSmartUserList } from "@/domain/user/hooks/api/use-smart-user-list";

import { formatUnitLabel } from "../../lib/field-format";
import { tryParseJson } from "../../lib/try-parse-json";
import type { DataRequestFieldType } from "../create-data-request-dialog/data-request-fields";

export function useFieldValueResolver({ workspaceId }: { workspaceId: string }) {
  const { statusList } = useGetStatusListForWorkspace({ workspaceId });
  const { priorityList } = useGetPriorityListForWorkspace({ workspaceId });
  const { phaseList } = useGetPhaseListForWorkspace({ workspaceId });
  const { focusAreaList } = useGetFocusAreaList({ workspaceId });
  const { userList } = useSmartUserList({ workspaceId });
  const { labelList } = useGetLabelListForWorkspace({ workspaceId });
  const { labelGroupList } = useGetLabelGroupListForWorkspace({ workspaceId });
  const { unitList } = useGetUnitList({ workspaceId });

  const statusMap = useMemo(() => new Map(statusList.map((s) => [s.id, s.name])), [statusList]);
  const priorityMap = useMemo(() => new Map(priorityList.map((p) => [p.id, p.name])), [priorityList]);
  const phaseMap = useMemo(() => new Map(phaseList.map((p) => [p.id, p.name])), [phaseList]);
  const focusAreaMap = useMemo(() => new Map(focusAreaList.map((f) => [f.id, f.name])), [focusAreaList]);
  const userMap = useMemo(() => new Map(userList.map((u) => [u.id, u.fullname ?? u.email])), [userList]);
  const labelGroupMap = useMemo(() => new Map(labelGroupList.map((g) => [g.id, g.title])), [labelGroupList]);
  const labelMap = useMemo(
    () =>
      new Map(
        labelList.map((l) => {
          const groupTitle = l.labelGroupId ? labelGroupMap.get(l.labelGroupId) : null;
          const displayTitle = groupTitle ? `${groupTitle}: ${l.title}` : l.title;
          return [l.id, displayTitle];
        }),
      ),
    [labelList, labelGroupMap],
  );
  const unitMap = useMemo(
    () => new Map(unitList.map((u) => [u.id, formatUnitLabel(u.title, u.symbol)])),
    [unitList],
  );

  const resolve = useCallback(
    (fieldType: DataRequestFieldType, rawValue: unknown): string => {
      switch (fieldType) {
        case "richtext": {
          const obj = typeof rawValue === "object" && rawValue !== null ? rawValue : tryParseJson(rawValue);
          if (obj && typeof obj === "object" && "text" in obj) {
            return (obj as { text: string }).text ?? "";
          }
          return String(rawValue);
        }
        default:
          break;
      }

      const value = String(rawValue);

      switch (fieldType) {
        case "status-select":
          return statusMap.get(value) ?? value;
        case "priority-select":
          return priorityMap.get(value) ?? value;
        case "phase-select":
          return phaseMap.get(value) ?? value;
        case "focus-area-select":
          return focusAreaMap.get(value) ?? value;
        case "user-select":
          return value
            .split(",")
            .filter(Boolean)
            .map((id) => userMap.get(id) ?? id)
            .join(", ");
        case "label-select":
          return value
            .split(",")
            .filter(Boolean)
            .map((id) => labelMap.get(id) ?? id)
            .join(", ");
        case "unit-select":
          return value === "" ? "—" : (unitMap.get(value) ?? value);
        case "date": {
          const date = new Date(value);
          return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("de-DE");
        }
        case "number": {
          const num = Number(value);
          return Number.isNaN(num) ? value : num.toLocaleString("de-DE");
        }
        default:
          return value;
      }
    },
    [statusMap, priorityMap, phaseMap, focusAreaMap, userMap, labelMap, unitMap],
  );

  return { resolve };
}
